import { useState } from 'react';
import { Settings, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useBaby } from '@/contexts/BabyContext';
import BabySwitcher from './BabySwitcher';
import babyBoyAvatar from '@/assets/baby_boy.webp';
import babyGirlAvatar from '@/assets/baby-girl-avatar.webp';

interface HeaderProps {
  /**
   * When set, render the slim variant: small avatar + page title + gear, on
   * a single short row. Saves ~70px of vertical space vs the default greeting
   * header. Use on every tab that isn't Today.
   */
  title?: string;
}

const Header = ({ title }: HeaderProps) => {
  const navigate = useNavigate();
  const { activeBaby, babies } = useBaby();
  const [showSwitcher, setShowSwitcher] = useState(false);

  const firstName = activeBaby?.name?.split(' ')[0] ?? 'there';
  const showSwitcherButton = babies.length > 1;
  const compact = !!title;

  const avatarSrc =
    activeBaby?.photoURL ||
    (activeBaby?.gender === 'boy' ? babyBoyAvatar : babyGirlAvatar);

  const avatarSize = compact ? 'w-8 h-8' : 'w-11 h-11';

  return (
    <div
      className={`header-gradient px-5 lg:hidden ${compact ? 'pb-3' : 'pb-5'}`}
      style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}
    >

      <div className="flex items-center gap-3">

        {/* Avatar (with optional baby switcher button) */}
        {showSwitcherButton ? (
          <button
            onClick={() => setShowSwitcher(true)}
            className="avatar-ring flex-shrink-0 active:opacity-80"
          >
            <img
              src={avatarSrc}
              alt={activeBaby?.name || 'Baby'}
              className={`${avatarSize} rounded-full object-cover block`}
            />
          </button>
        ) : (
          <div className="avatar-ring flex-shrink-0">
            <img
              src={avatarSrc}
              alt={activeBaby?.name || 'Baby'}
              className={`${avatarSize} rounded-full object-cover block`}
            />
          </div>
        )}

        {/* Title block — compact uses page title; full uses greeting */}
        <div className="min-w-0 flex-1">
          {compact ? (
            <h1 className="text-[18px] font-bold text-foreground leading-tight truncate">
              {title}
            </h1>
          ) : (
            <>
              <p
                className="text-muted-foreground"
                style={{ fontSize: 12, fontWeight: 400 }}
              >
                Welcome back!
              </p>

              {showSwitcherButton ? (
                <button
                  onClick={() => setShowSwitcher(true)}
                  className="flex items-center gap-1 mt-0.5"
                >
                  <h1 className="text-[24px] font-bold text-foreground leading-tight truncate">
                    Hi {firstName}
                  </h1>
                  <ChevronDown className="w-5 h-5 text-foreground/60 flex-shrink-0" />
                </button>
              ) : (
                <h1 className="text-[24px] font-bold text-foreground leading-tight mt-0.5">
                  Hi {firstName}
                </h1>
              )}
            </>
          )}
        </div>

        {/* Settings gear */}
        <button
          onClick={() => navigate('/settings')}
          className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: 'rgba(255, 255, 255, 0.70)' }}
        >
          <Settings className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>

      <BabySwitcher isOpen={showSwitcher} onClose={() => setShowSwitcher(false)} />
    </div>
  );
};

export default Header;
